import { useCallback, useEffect, useRef, useState } from 'react';

import { useLayoutData } from '@eneco/cms';
import logger from '@eneco/common/log';
import { keepCmsOverride, Route, RouterProvider, Routes, useRouter } from '@eneco/next';
import { SkipToMain } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';

import { useFlowHooks } from './FlowProvider';
import { FlowRedirect } from './FlowRedirect';
import { getEventTypeByPath } from './FlowUtils';
import { waitForFlowStepHeadingFocus } from './focusFlowStepHeading';
import { useFlowBasket } from '../hooks/useFlowBasket';
import { useFlowQueryParams } from '../hooks/useFlowQueryParams';
import { toFlowRootPath, usePageContent } from '../hooks/usePageContent';
import { FlowContext } from '../types';
import type { FlowRoute } from '../types/machine';

interface Props<T extends FlowContext> {
  routes: FlowRoute<T>[];
  stickyQueryParams?: Array<string>;
  completionQueryParams?: (context: T) => Record<string, string | number | boolean | undefined>;
}

const RouterConsumer = <T extends FlowContext>({ routes, stickyQueryParams, completionQueryParams }: Props<T>) => {
  useFlowQueryParams();
  useFlowBasket();

  const { trackPageView, trackFunnelStart, trackFunnelStep, trackFunnelCompleted } = useTracking();
  const { useFlowSelector, useFlowActorRef } = useFlowHooks();
  const { send } = useFlowActorRef();
  const flowState = useFlowSelector(state => state);
  const currentRoute = useFlowSelector(state => state.context.path);
  const previousRoute = useFlowSelector(state => state.context.pathPrevious);

  const { frontEndRootPath, flowCompletionUrl, flowName } = usePageContent();
  const { experimentInfo, pagePath } = useLayoutData();
  const variant = experimentInfo?.variant;

  const router = useRouter();
  const { activeRoute, routerBasePath } = router;

  const [isInitialized, setIsInitialized] = useState(false);
  const [isCompleted, setIsCompleted] = useState(flowState.matches('COMPLETED'));
  const [isActive, setIsActive] = useState(false);
  const hasFocusedInitialStep = useRef(false);

  // Capture initial query parameters once and preserve them throughout the flow
  const [initialQueryParams, setInitialQueryParams] = useState<string>('');

  useEffect(() => {
    if (typeof window !== 'undefined' && !initialQueryParams) {
      const search = window.location.search;
      setInitialQueryParams(search);
    }
  }, [initialQueryParams]);

  const redirect = useCallback(() => {
    // Flat content reaches the href directly. This used to need a double cast
    // through `unknown`, because the Sitecore field type declared `value` as a
    // string while the payload actually carried a link object.
    if (flowCompletionUrl?.href) {
      const completionUrl = new URL(flowCompletionUrl.href, window.location.origin);
      const queryParams = completionQueryParams?.(flowState.context as unknown as T);

      Object.entries(queryParams ?? {}).forEach(([key, value]) => {
        if (value !== undefined) {
          completionUrl.searchParams.set(key, String(value));
        }
      });

      // The lane override goes along, as it does to every step (see below), by the rule every redirect on the site
      // follows. It is read from the current address, which carries it on every step, so it also holds when a
      // finished flow redirects straight on load.
      window.location.href = keepCmsOverride(completionUrl.toString());
    }
  }, [completionQueryParams, flowCompletionUrl, flowState.context]);

  useEffect(() => {
    setIsCompleted(flowState.matches('COMPLETED'));
  }, [flowState]);

  useEffect(() => {
    isInitialized &&
      trackFunnelStart({
        funnel: flowName,
        step: getEventTypeByPath(routes, currentRoute),
      });
  }, [isInitialized]);

  useEffect(() => {
    isCompleted &&
      isInitialized &&
      trackFunnelCompleted({
        funnel: flowName,
        step: 'STEP_THANK_YOU',
      });
    isCompleted && redirect();
  }, [isCompleted]);

  useEffect(() => {
    setIsActive(isInitialized && !isCompleted);
  }, [isInitialized, isCompleted]);

  useEffect(() => {
    if (!isActive || activeRoute !== currentRoute) return;

    // Keep skip-to-main as the first tab stop on the initial page load.
    if (!hasFocusedInitialStep.current) {
      hasFocusedInitialStep.current = true;
      return;
    }

    return waitForFlowStepHeadingFocus();
  }, [activeRoute, currentRoute, isActive]);

  useEffect(() => {
    if (currentRoute) {
      const basePath = routerBasePath.endsWith('/') ? routerBasePath.slice(0, -1) : routerBasePath;

      // Construct path with query params only when needed
      let path = currentRoute;

      // Stripped up front so the empty case is visible: where the Sitecore hook
      // gave `undefined` for a missing item path, the CMS adapter substitutes
      // '/', which strips to '' and would make `endsWith` trivially true against
      // any root path. Requiring a non-empty value keeps the previous outcome.
      const currentPath = pagePath?.replaceAll('/', '') ?? '';
      const shouldAddVariantTracking =
        variant && currentPath && toFlowRootPath(frontEndRootPath)?.replaceAll('/', '').endsWith(currentPath);

      const hasStickyParams = initialQueryParams && stickyQueryParams?.length;

      // The `?_cms=` lane override has to survive the flow's own navigation, or the first step
      // lands on the default provider and the rest of the flow is served by a different CMS than
      // the page the visitor started on. Sticky for every flow rather than per-flow config,
      // because it selects the provider rather than saying anything about this flow. Where it
      // takes effect is decided on the server: a deployed build honours it only in a container
      // that passes `allowCmsOverrideInProduction` to `getSSP`, and `is-sanity-enabled.ts` opens
      // Sanity on development, test and local, and on acceptance and production as well for a
      // container with `allowSharedEnvironment` (mkb has it, for the Studio's preview).
      const laneOverride = new URLSearchParams(initialQueryParams).get('_cms');

      // Only create URL search params if we need to modify the path
      if (hasStickyParams || shouldAddVariantTracking || laneOverride) {
        const searchParams = new URLSearchParams();

        // Add sticky query parameters
        if (hasStickyParams) {
          const initialParams = new URLSearchParams(initialQueryParams);
          initialParams.forEach((value, key) => {
            if (stickyQueryParams.includes(key)) {
              searchParams.set(key, value);
            }
          });
        }

        if (laneOverride) {
          searchParams.set('_cms', laneOverride);
        }

        // Add variant tracking if needed
        if (shouldAddVariantTracking) {
          searchParams.set('sc_trk', variant);
        }

        // Combine currentRoute with search parameters
        const searchString = searchParams.toString();
        path = searchString ? `${currentRoute}?${searchString}` : currentRoute;
      }

      // Reported once the query string (sticky params, sc_trk) is resolved, so
      // page_location on the eager page_view matches the URL router.push is
      // about to commit — see docs/tracking.md#callers-that-navigate-themselves.
      trackPageView({
        pageName: `${basePath}${path}`,
        previousPage: `${window?.location?.origin}${basePath}${previousRoute ?? '/'}`,
      });

      if (isInitialized) {
        router.push(path);
      } else if (activeRoute !== currentRoute) {
        router.replace(path);
      }
    }
  }, [currentRoute, initialQueryParams]);

  useEffect(() => {
    currentRoute &&
      previousRoute &&
      currentRoute !== previousRoute &&
      trackFunnelStep({
        funnel: flowName,
        step: getEventTypeByPath(routes, currentRoute),
      });
  }, [currentRoute, previousRoute]);

  useEffect(() => {
    if (currentRoute && currentRoute !== activeRoute) {
      send({
        type: 'GOTO',
        stepName: getEventTypeByPath(routes, activeRoute?.toString()),
      });
    }

    if (isCompleted) {
      redirect();
    }
  }, [activeRoute]);

  useEffect(() => {
    if (activeRoute === currentRoute) {
      setIsInitialized(true);
    }
  }, [activeRoute, currentRoute]);

  if (isActive) {
    return (
      <Routes>
        {routes.map(({ path, component }) => (
          <Route key={path} route={path} component={component} />
        ))}
      </Routes>
    );
  }

  if (isCompleted) {
    return (
      <Routes>
        <Route route={activeRoute ?? '/'} component={FlowRedirect} />
      </Routes>
    );
  }

  return <SkipToMain.Main />;
};

export const FlowRouter = <T extends FlowContext>({ routes, stickyQueryParams, completionQueryParams }: Props<T>) => {
  // The flow's own page says where it is rooted, so the router never reads Sitecore layout data.
  // That hook throws rather than defaulting when no Sitecore context is mounted, which is every
  // render on the CMS-agnostic lane.
  const { frontEndRootPath, allowFrontEndRouting } = usePageContent();

  // Checked rather than trusted: see `toRootPath`.
  const rootPath = toFlowRootPath(frontEndRootPath);

  if (!rootPath) {
    // Every page of a flow carries a usable value on Sitecore. Without one the flow routes from the
    // site root and matches no step, so name what did arrive rather than leaving a blank screen.
    logger.error('fLwRp1', 'Flow page carries no usable frontEndRootPath, so the flow cannot route', {
      received: typeof frontEndRootPath,
    });
  }

  return (
    <RouterProvider basePath={rootPath ?? '/'} allowFrontEndRouting={allowFrontEndRouting}>
      <RouterConsumer<T>
        routes={routes}
        stickyQueryParams={stickyQueryParams}
        completionQueryParams={completionQueryParams}
      />
    </RouterProvider>
  );
};
